import React, { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Divider,
    Grid,
    CircularProgress,
    Avatar,
    Paper,
    TextField
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Person as PersonIcon,
    Block as BlockIcon,
    CheckCircle as ActiveIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    AccountBalance as AccountBalanceIcon,
    CalendarToday as CalendarIcon,
    Close as CloseIcon,
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const UserActionMenu = ({ user, onStatusChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(user);
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [fullUserData, setFullUserData] = useState(null);
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationContent, setNotificationContent] = useState('');
    const [sendingNotification, setSendingNotification] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = async (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);

        // Fetch latest user details to ensure correct status
        if (user && user._id) {
            try {
                const updatedUser = await api.dashboard.getUserById(user._id);
                if (updatedUser) {
                    setCurrentUser(updatedUser);
                }
            } catch (error) {
                console.error("Failed to fetch fresh user details", error);
            }
        }
    };

    const handleClose = (event) => {
        if (event) event.stopPropagation();
        setAnchorEl(null);
    };

    const handleViewProfile = async (event) => {
        handleClose(event);
        if (!currentUser || !currentUser._id) {
            toast.error("User ID not found");
            return;
        }

        setProfileModalOpen(true);
        setLoadingProfile(true);

        try {
            const userData = await api.dashboard.getUserById(currentUser._id);
            setFullUserData(userData);
        } catch (error) {
            console.error("Failed to fetch user profile", error);
            toast.error("Failed to load user profile");
        } finally {
            setLoadingProfile(false);
        }
    };

    const handleCloseProfileModal = () => {
        setProfileModalOpen(false);
        setFullUserData(null);
    };

    const handleToggleStatus = async (event) => {
        handleClose(event);
        if (!currentUser || !currentUser._id) return;

        const newStatus = currentUser.status === 'suspended' ? 'active' : 'suspended';
        const actionName = newStatus === 'active' ? 'activate' : 'suspend';

        if (!window.confirm(`Are you sure you want to ${actionName} this user?`)) {
            return;
        }

        setLoading(true);
        try {
            const response = await api.dashboard.updateUserStatus(currentUser._id, newStatus);
            if (response.status) {
                toast.success(`User ${actionName}d successfully`);
                // Update internal state
                setCurrentUser(prev => ({ ...prev, status: newStatus }));

                // Log activity
                await api.dashboard.logAdminActivity({
                    action: newStatus === 'active' ? 'USER_ACTIVATED' : 'USER_SUSPENDED',
                    details: `${newStatus === 'active' ? 'Activated' : 'Suspended'} user ${currentUser.username || currentUser.email}`,
                    targetId: currentUser._id,
                    targetType: 'user'
                });

                if (onStatusChange) {
                    onStatusChange(currentUser._id, newStatus);
                }
            } else {
                toast.error(response.message || `Failed to ${actionName} user`);
            }
        } catch (error) {
            console.error(error);
            toast.error(`Error: ${error.message || "Something went wrong"}`);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenNotificationModal = (event) => {
        handleClose(event);
        setNotificationModalOpen(true);
    };

    const handleCloseNotificationModal = () => {
        setNotificationModalOpen(false);
        setNotificationTitle('');
        setNotificationContent('');
    };

    const handleSendNotification = async () => {
        if (!notificationTitle || !notificationContent) {
            toast.error("Please provide both title and content");
            return;
        }

        setSendingNotification(true);
        try {
            const response = await api.dashboard.sendNotification(currentUser._id, notificationTitle, notificationContent);
            if (response.success) {
                toast.success("Notification sent successfully");
                handleCloseNotificationModal();
            } else {
                toast.error(response.message || "Failed to send notification");
            }
        } catch (error) {
            console.error("Error sending notification:", error);
            toast.error(error.message || "Something went wrong");
        } finally {
            setSendingNotification(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatCurrency = (amount) => {
        return `₦${Number(amount || 0).toLocaleString()}`;
    };

    return (
        <React.Fragment>
            <Tooltip title="User Actions">
                <IconButton
                    aria-label="more"
                    id={`user-action-button-${currentUser?._id}`}
                    aria-controls={open ? 'user-action-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                    size="small"
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Action Menu */}
            {currentUser && currentUser._id && (
                <Menu
                    id="user-action-menu"
                    MenuListProps={{
                        'aria-labelledby': `user-action-button-${currentUser._id}`,
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuItem onClick={handleViewProfile}>
                        <ListItemIcon>
                            <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>View Profile</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleToggleStatus} disabled={loading}>
                        <ListItemIcon>
                            {currentUser.status === 'suspended' ?
                                <ActiveIcon fontSize="small" color="success" /> :
                                <BlockIcon fontSize="small" color="error" />
                            }
                        </ListItemIcon>
                        <ListItemText>
                            {currentUser.status === 'suspended' ? 'Activate User' : 'Suspend User'}
                        </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleOpenNotificationModal}>
                        <ListItemIcon>
                            <NotificationsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText>Send Notification</ListItemText>
                    </MenuItem>
                </Menu>
            )}

            {/* Profile Modal */}
            <Dialog
                open={profileModalOpen}
                onClose={handleCloseProfileModal}
                maxWidth="md"
                fullWidth
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                        background: (theme) => theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
                            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: (theme) => theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.05)',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                        color: 'white',
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PersonIcon sx={{ fontSize: 28 }} />
                        <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
                            User Profile
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseProfileModal}
                        size="small"
                        sx={{
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                transform: 'rotate(90deg)',
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {loadingProfile ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress size={60} thickness={4} />
                        </Box>
                    ) : fullUserData ? (
                        <Box>
                            {/* User Header with Gradient Background */}
                            <Box
                                sx={{
                                    background: 'linear-gradient(135deg, rgba(168, 10, 10, 0.1) 0%, rgba(255, 107, 107, 0.1) 100%)',
                                    p: 4,
                                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Avatar
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                                                fontSize: '2rem',
                                                fontWeight: 700,
                                                boxShadow: '0 8px 24px rgba(168, 10, 10, 0.3)',
                                                border: '4px solid white'
                                            }}
                                        >
                                            {(fullUserData.fullname || fullUserData.username || 'U').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                bgcolor: fullUserData.status === 'suspended' ? 'error.main' : 'success.main',
                                                border: '3px solid white',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h4" fontWeight={700} gutterBottom>
                                            {fullUserData.fullname || fullUserData.username || 'Unknown User'}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                            @{fullUserData.username || 'N/A'}
                                        </Typography>
                                        <Chip
                                            label={fullUserData.status === 'suspended' ? 'Suspended' : 'Active'}
                                            size="medium"
                                            icon={fullUserData.status === 'suspended' ? <BlockIcon /> : <ActiveIcon />}
                                            color={fullUserData.status === 'suspended' ? 'error' : 'success'}
                                            sx={{
                                                fontWeight: 600,
                                                px: 2,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* User Details Cards */}
                            <Box sx={{ p: 4 }}>
                                <Grid container spacing={3}>
                                    {/* Contact Information Card */}
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                borderRadius: 3,
                                                background: (theme) => theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(168, 10, 10, 0.03)',
                                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                                                Contact Information
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: 2,
                                                            bgcolor: 'primary.main',
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <EmailIcon fontSize="small" />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            Email Address
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {fullUserData.email || 'Not provided'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Divider />
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: 2,
                                                            bgcolor: 'success.main',
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <PhoneIcon fontSize="small" />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            Phone Number
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {fullUserData.phoneNumber || 'Not provided'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    {/* Account Information Card */}
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                borderRadius: 3,
                                                background: (theme) => theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(168, 10, 10, 0.03)',
                                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                                                Account Details
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: 2,
                                                            bgcolor: 'warning.main',
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <AccountBalanceIcon fontSize="small" />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            Current Balance
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700} color="primary.main">
                                                            {formatCurrency(fullUserData.balance)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Divider />
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: 2,
                                                            bgcolor: 'info.main',
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <CalendarIcon fontSize="small" />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            Member Since
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {formatDate(fullUserData.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    {/* Additional Information */}
                                    {(fullUserData.kycVerified !== undefined || fullUserData.referralCode) && (
                                        <Grid item xs={12}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    borderRadius: 3,
                                                    background: (theme) => theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(168, 10, 10, 0.03)',
                                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                                }}
                                            >
                                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                                                    Additional Information
                                                </Typography>
                                                <Grid container spacing={3}>
                                                    {fullUserData.kycVerified !== undefined && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom display="block">
                                                                    KYC Verification
                                                                </Typography>
                                                                <Chip
                                                                    label={fullUserData.kycVerified ? 'Verified ✓' : 'Not Verified'}
                                                                    size="medium"
                                                                    color={fullUserData.kycVerified ? 'success' : 'default'}
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        mt: 1,
                                                                        boxShadow: fullUserData.kycVerified ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    {fullUserData.referralCode && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom display="block">
                                                                    Referral Code
                                                                </Typography>
                                                                <Typography
                                                                    variant="h6"
                                                                    fontWeight={700}
                                                                    sx={{
                                                                        mt: 1,
                                                                        color: 'primary.main',
                                                                        fontFamily: 'monospace',
                                                                        letterSpacing: 2
                                                                    }}
                                                                >
                                                                    {fullUserData.referralCode}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary" variant="h6">
                                No user data available
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 3,
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        background: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(0, 0, 0, 0.02)',
                    }}
                >
                    <Button
                        onClick={handleCloseProfileModal}
                        variant="contained"
                        size="large"
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                            boxShadow: '0 4px 12px rgba(168, 10, 10, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #8B0000 0%, #E55A5A 100%)',
                                boxShadow: '0 6px 16px rgba(168, 10, 10, 0.4)',
                                transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Dialog */}
            <Dialog
                open={notificationModalOpen}
                onClose={handleCloseNotificationModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <NotificationsIcon />
                    Send Notification
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Send a custom notification to <strong>{currentUser?.username || 'this user'}</strong>. This will appear in their in-app notifications and as a push notification if enabled.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Notification Title"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={notificationTitle}
                        onChange={(e) => setNotificationTitle(e.target.value)}
                        placeholder="e.g. Account Update"
                        sx={{ mb: 2, mt: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Notification Message"
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={notificationContent}
                        onChange={(e) => setNotificationContent(e.target.value)}
                        placeholder="Type your message here..."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseNotificationModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendNotification}
                        variant="contained"
                        disabled={sendingNotification || !notificationTitle || !notificationContent}
                        startIcon={sendingNotification ? <CircularProgress size={20} color="inherit" /> : <NotificationsIcon />}
                    >
                        {sendingNotification ? 'Sending...' : 'Send Notification'}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default UserActionMenu;
